import { Logo } from '@/components';
import InvitationDialog from '@/components/InvitationDialog';
import { useSnackbar } from '@/context/SnackbarContext';
import { useCommonCurrentUser, useCurrentStudentUserShort, useMagicLinkUsage, useMagicLinkValidation } from '@/utils/api';
import { signOut, useFirebaseAuth } from '@/utils/firebase';
import { initDevTools } from '@/utils/inits';
import { getInvitationErrorMessage } from '@/utils/invitationUtils';
import {
  Box,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  CalendarIcon,
  ChatCircleTextIcon,
  FileTextIcon,
  GraduationCapIcon,
  LifebuoyIcon,
  ListChecksIcon,
  MoneyIcon,
  RankingIcon,
  SignOutIcon,
  UserIcon,
  UserPlusIcon
} from '@phosphor-icons/react';
import { IconButton } from '@repo/ui';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { SnackbarProvider } from '../context/SnackbarContext';
import TutorialDialog from '../pages/app/profile/components/TutorialDialog';
import AuthWrapper from './AuthWrapper';
import MagicLoading from './MagicLoading.js';

// Define the sidebar width
const drawerWidth = 280;

// Interface for navigation item
interface NavItem {
  title: string;
  path: string;
  icon: React.ReactNode;
  authRequired?: boolean;
}

// Interface for navigation section
interface NavSection {
  title: string;
  items: NavItem[];
  authRequired?: boolean
}

interface AppLayoutProps {
  withAuth?: boolean;
}


// Create a styled component for the main content
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  height: '100vh',
  overflow: 'auto',
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}));

/**
 * Layout for authenticated app pages
 */
const AppLayout: React.FC<AppLayoutProps> = ({ withAuth = false }) => {
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const location = useLocation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { isInitialized, user: firebaseUser } = useFirebaseAuth();

  // Use common endpoint for routing decisions
  const { data: commonUser, isLoading: isCommonLoading, isError: isCommonError } = useCommonCurrentUser({
    enabled: isInitialized && !!firebaseUser,
    showErrorSnackbar: false
  });

  // Use student-specific endpoint only when we know user is student
  const { data: user, isLoading: isStudentLoading, isError: isStudentError } = useCurrentStudentUserShort({
    enabled: isInitialized && !!firebaseUser && commonUser?.data?.userType === 'student',
    showErrorSnackbar: false
  });

  const isUserLoading = isCommonLoading || (commonUser?.data?.userType === 'student' && isStudentLoading);
  const isUserError = isCommonError || (commonUser?.data?.userType === 'student' && isStudentError);
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('md'));

  // ============= MAGIC LINK INVITATION LOGIC =============
  const [invitationToken, setInvitationToken] = useState<string | null>(null);
  const [showInvitationDialog, setShowInvitationDialog] = useState(false);
  const [invitationError, setInvitationError] = useState<string | null>(null);

  // Check localStorage for magic link token
  useEffect(() => {
    const token = localStorage.getItem('magic_link_token');
    if (token) {
      setInvitationToken(token);
    }
  }, []);

  // Validate magic link token
  const { data: validationData } = useMagicLinkValidation(invitationToken, {
    enabled: !!invitationToken &&
      isInitialized &&
      !!firebaseUser &&
      commonUser?.data?.userType === 'student' &&
      !user?.data?.organizationId,
    showErrorSnackbar: false,
  });

  // Show dialog if validation succeeds
  useEffect(() => {
    if (validationData?.data?.isValid && invitationToken && !user?.data?.organizationId) {
      setShowInvitationDialog(true);
    }
  }, [validationData, invitationToken, user?.data?.organizationId]);

  // Apply magic link invitation
  const applyInvitation = useMagicLinkUsage({
    showErrorSnackbar: false,
  });

  const handleAcceptInvitation = useCallback(async () => {
    if (!invitationToken) return;

    setInvitationError(null);

    try {
      const result = await applyInvitation.mutateAsync({ token: invitationToken });

      if (result.success && result.data.success) {
        // Clear token from localStorage
        localStorage.removeItem('magic_link_token');
        setInvitationToken(null);
        setShowInvitationDialog(false);

        // Show success message
        showSnackbar(
          `Successfully joined ${validationData?.data?.organization?.name || 'your school'}! 🎉`,
          'success'
        );
      } else {
        // Handle failure
        const errorMsg = getInvitationErrorMessage(result.data?.message || 'Failed to apply invitation');
        setInvitationError(errorMsg);
      }
    } catch (error) {
      console.error('Error applying invitation:', error);
      const errorMsg = error instanceof Error
        ? getInvitationErrorMessage(error.message)
        : 'Unable to apply invitation. Please try again.';
      setInvitationError(errorMsg);
    }
  }, [invitationToken, applyInvitation, showSnackbar, validationData]);

  const handleDeclineInvitation = useCallback(() => {
    // Clear token from localStorage
    localStorage.removeItem('magic_link_token');
    setInvitationToken(null);
    setShowInvitationDialog(false);
    setInvitationError(null);
  }, []);
  // ============= END MAGIC LINK INVITATION LOGIC =============

  useEffect(() => {
    if (!isInitialized || !firebaseUser || isUserLoading) return;

    if (commonUser?.data?.cookies?.developerAid) {
      initDevTools();
    }

    // Handle role-based routing first with common user data
    if (commonUser?.data) {
      if (commonUser.data.userType === 'staff') {
        navigate('/school/app');
        return;
      }
    }

    // Handle student-specific logic
    if (user) {
      if (!user.data.lastName || !user.data.firstName) {
        // User object completed, but not fully setup
        navigate('/auth/signup/details');
      }
    } else if (commonUser?.data?.userType === 'student') {
      // We have a firebase user and they're a student, but no student profile yet
      navigate('/auth/signup/details');
    } else if (isUserError) {
      // Error loading user data, might need to setup
      navigate('/auth/signup/details');
    }
  }, [isInitialized, firebaseUser, user, commonUser, navigate, isUserError, isUserLoading])

  // Check for tutorial flag on mount
  useEffect(() => {
    const showTutorial = localStorage.getItem('SHOW_TUTORIAL');
    if (showTutorial === 'true') {
      setTutorialOpen(true);
      localStorage.removeItem('SHOW_TUTORIAL');
    }
  }, []);

  const handleLogout = () => {
    signOut();
    queryClient.clear()
    navigate('/auth/login');
  };

  const handleTutorialOpen = () => {
    setTutorialOpen(true);
  };

  const handleTutorialClose = () => {
    setTutorialOpen(false);
  };

  const navSections: NavSection[] = [
    {
      title: 'Personal',
      items: [
        { title: 'Your Profile', path: '/app/profile', icon: <UserIcon size={24} /> },
        { title: 'Your Calendar', path: '/app/calendar', icon: <CalendarIcon size={24} /> },
      ],
      authRequired: true
    },
    {
      title: 'Search',
      items: [
        { title: 'Universities', path: '/app/courses/discover', icon: <GraduationCapIcon size={24} /> },
        { title: 'Short Courses', path: '/app/short-courses/discover', icon: <RankingIcon size={24} /> },
        { title: 'Scholarships', path: '/app/scholarships/discover', icon: <MoneyIcon size={24} /> },
        { title: 'Watchlist', path: '/app/watchlist', icon: <ListChecksIcon size={24} />, authRequired: true },
      ]
    },
    {
      title: 'Essays',
      items: [
        { title: 'Personal Statements', path: '/app/essays', icon: <FileTextIcon size={24} /> },
      ]
    }
  ]



  // Block rendering until auth initialized and user query settled
  if (!isInitialized || isUserLoading) {
    return (
      <MagicLoading
        active
        onMagic={() => {
          signOut();
          navigate('/auth/login');
        }}
      />
    );
  }

  if (isMobile) {
    return <Stack direction='column' alignItems='center' justifyContent='center' height='100vh' p={2} textAlign='center'>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
        🚧 Thank you for joining Zube AI!
      </Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        📱 Mobile support is coming soon. For the best experience, please use a desktop or laptop browser.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        🙏 We appreciate your patience as we build a better mobile experience!
      </Typography>
    </Stack>
  }


  return (
    <SnackbarProvider>
      {validationData?.data && <InvitationDialog
        open={showInvitationDialog}
        invitation={validationData.data}
        isApplying={applyInvitation.isPending}
        error={invitationError}
        onAccept={handleAcceptInvitation}
        onDecline={handleDeclineInvitation}
      />}
      <Stack direction="row">
        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            height: '100%',
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              border: 'none',
              borderRight: '1px solid rgba(0, 0, 0, 0.1)',
              boxShadow: 'none',
              height: '100%',
            },
          }}
          variant="persistent"
          anchor="left"
          open={true}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              padding: theme => theme.spacing(2),
              height: 80,
            }}>
            <Logo />
            <Box
              onClick={() => navigate('/app/chat')}
              sx={{
                width: 48,
                height: 48,
                border: '1px solid transparent',
                borderRadius: '50%',
                background: 'linear-gradient(45deg, #2196F3, #FF9800)',
                padding: '3px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                outline: 'none',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  scale: 1.3
                },
                '&:focus': {
                  outline: 'none',
                },
              }}
            >
              <IconButton
                tooltip="Talk to Zube"
                sx={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'white',
                  outline: 'none',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&:focus': {
                    outline: 'none',
                  },
                  borderRadius: '50%',
                }}
              >
                <ChatCircleTextIcon size={48} />
              </IconButton>
            </Box>
          </Stack>
          <Stack sx={{ overflow: 'hidden', height: '100%', padding: 2 }}>
            <Box sx={{ overflow: 'auto' }}>
              {navSections.map((section, outerIndex) => (
                (!(section.authRequired || section.items.every(item => item.authRequired)) || user) && (<React.Fragment key={section.title}>
                  {/* {section.title.length > 0 && <Typography
                    variant="h6"
                    sx={{
                      fontSize: '1.1rem',
                      color: 'text.primary'
                    }}
                  >
                    {section.title}
                  </Typography>} */}
                  {outerIndex > 0 && <Divider />}
                  <List sx={{ pl: 0, pb: 2 }}>
                    {section.items.filter(item => !item.authRequired || user).map((item) => {
                      const isSelected = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                      return (
                        <ListItem key={item.path} disablePadding>
                          <ListItemButton
                            component={Link}
                            to={item.path}
                            sx={{
                              borderRadius: '8px',
                              py: 0.5,
                              px: 1,
                              my: 0.5,
                              backgroundColor: isSelected ? 'rgba(242, 242, 242, 0.8)' : 'transparent',
                              '&:hover': {
                                backgroundColor: isSelected ? 'rgba(242, 242, 242, 0.8)' : 'rgba(0, 0, 0, 0.04)'
                              }
                            }}
                          >
                            <ListItemIcon sx={{
                              minWidth: 40,
                              color: isSelected ? 'primary.main' : 'text.secondary'
                            }}>
                              {item.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={item.title}
                              slotProps={{
                                primary: {
                                  color: 'text.primary',
                                }
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </React.Fragment>)
              ))}
            </Box>
            <Box flex={1} />

            {/* Referral Promotion Box */}
            {!user || !user.data.organizationId && <Box
              component={Link}
              to="/app/referrals"
              sx={{
                display: user ? 'block' : 'none',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: 2,
                p: 2,
                mb: 2,
                cursor: 'pointer',
                transition: 'transform 0.2s ease-in-out',
                textDecoration: 'none',
                color: 'inherit',
                '&:hover': {
                  transform: 'translateY(-2px)',
                }
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box>
                  <Typography
                    variant='h6'
                    sx={{
                      fontSize: '1.1rem',
                      color: 'white',
                      fontWeight: 600,
                    }}
                  >
                    🎉 Refer Friends
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontSize: '0.75rem'
                    }}
                  >
                    Share with 10 of your friends to get a free university counselling session
                  </Typography>
                </Box>
              </Stack>
            </Box>}

            <ListItem disablePadding>
              <ListItemButton
                onClick={() => handleTutorialOpen()}
                sx={{
                  borderRadius: '8px',
                  py: 1.5,
                  background: 'linear-gradient(135deg, #fb923c 0%, #ea580c 100%)',
                }}
              >
                <ListItemIcon sx={{
                  minWidth: 40,
                  color: 'white'
                }}>
                  <LifebuoyIcon size={24} />
                </ListItemIcon>
                <ListItemText
                  primary="Tutorial"
                  slotProps={{
                    primary: {
                      color: 'white',
                    }
                  }}
                />
              </ListItemButton>
            </ListItem>
            {/* <ListItem disablePadding>
              <ListItemButton
                sx={{
                  borderRadius: '8px',
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                }}
              >
                <ListItemIcon sx={{
                  minWidth: 40,
                  color: 'text.secondary'
                }}>
                  <ThemeToggle size="medium" showTooltip={false} />
                </ListItemIcon>
                <ListItemText
                  primary="Theme"
                  slotProps={{
                    primary: {
                      color: 'text.primary',
                    }
                  }}
                />
              </ListItemButton>
            </ListItem> */}
            <ListItem disablePadding>
              <ListItemButton
                onClick={user ? handleLogout : () => navigate('/auth/login')}
                sx={{
                  borderRadius: '8px',
                  background: user ? undefined : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  py: 1.5,
                }}
              >
                <ListItemIcon sx={{
                  minWidth: 40,
                  color: user ? 'text.secondary' : 'white'
                }}>
                  {user ? <SignOutIcon size={24} /> : <UserPlusIcon size={24} />}
                </ListItemIcon>
                <ListItemText
                  primary={user ? 'Logout' : 'Login or Signup'}
                  slotProps={{
                    primary: {
                      color: user ? 'text.primary' : 'white',
                    }
                  }}
                />
              </ListItemButton>
            </ListItem>
          </Stack>
        </Drawer>
        <Main open={true} sx={{ padding: { xs: 2, md: 3, lg: 4, xl: 6 } }}>
          {withAuth ? <AuthWrapper>
            <Outlet />
          </AuthWrapper> : <Outlet />}
        </Main>
        <TutorialDialog
          open={tutorialOpen}
          onClose={handleTutorialClose}
        />
      </Stack>
    </SnackbarProvider>
  );
};

export default AppLayout; 